import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Container, 
  Alert, 
  CircularProgress,
  IconButton,
  Paper,
  Divider,
  AppBar,
  Toolbar,
  InputAdornment
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Edit, 
  Save, 
  Cancel
} from '@mui/icons-material';
import { updateProfile } from 'firebase/auth';
import UserProfile from '../components/UserProfile';
import { getBackendConfig } from '../config/backend';
import './Auth.css';

const ProfilePage: React.FC = () => {
  const { user, firebaseUser, loading: authLoading, sendPasswordReset, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sendingResetEmail, setSendingResetEmail] = useState(false);
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);
  const [checkingDisplayName, setCheckingDisplayName] = useState(false);
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckedValueRef = useRef<string>('');
  const lastErrorRef = useRef<string | null>(null);
  const userDisplayNameRef = useRef<string>('');

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setEmail(user.email || '');
      userDisplayNameRef.current = (user.displayName || '').trim().toLowerCase();
    }
  }, [user]);

  // Check display name availability when user stops typing
  useEffect(() => {
    // Only run validation when editing
    if (!isEditingName) {
      // Only reset if we were previously editing
      if (lastCheckedValueRef.current !== '') {
        setDisplayNameError(null);
        setCheckingDisplayName(false);
        lastCheckedValueRef.current = '';
        lastErrorRef.current = null;
      }
      return;
    }

    const trimmedName = displayName.trim();

    // Clear error if empty
    if (!trimmedName) {
      if (lastCheckedValueRef.current !== '') {
        setDisplayNameError(null);
        setCheckingDisplayName(false);
        lastCheckedValueRef.current = '';
        lastErrorRef.current = null;
      }
      return;
    }

    // Don't check if it's the same as the current display name (case-insensitive)
    if (trimmedName.toLowerCase() === userDisplayNameRef.current) {
      if (lastCheckedValueRef.current !== trimmedName) {
        setDisplayNameError(null);
        setCheckingDisplayName(false);
        lastCheckedValueRef.current = trimmedName;
        lastErrorRef.current = null;
      }
      return;
    }

    // If this is the same value we already checked, restore the last error state
    if (trimmedName === lastCheckedValueRef.current) {
      // Always restore the error state for this value - ref is source of truth
      console.log('🔄 Restoring error for already-checked value:', trimmedName, 'Error from ref:', lastErrorRef.current);
      // Force update to match ref value
      if (displayNameError !== lastErrorRef.current) {
        setDisplayNameError(lastErrorRef.current);
      }
      return; // Already checked this value, keep showing the last result
    }

    // Clear any pending validation for a different value
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }

    // Debounce: wait for user to stop typing
    validationTimeoutRef.current = setTimeout(async () => {
      const valueToCheck = trimmedName;
      
      // If the value changed while we were waiting, don't check
      if (displayName.trim() !== valueToCheck) {
        return;
      }

      console.log('🔍 Checking display name availability for:', valueToCheck);
      setCheckingDisplayName(true);
      
      try {
        const apiUrl = getBackendConfig().apiBaseUrl;
        
        const excludeUserId = user?.id ? user.id.toString() : undefined;
        const params = new URLSearchParams({
          displayName: valueToCheck,
          ...(excludeUserId && { excludeUserId })
        });

        console.log('📡 Fetching:', `${apiUrl}/auth/check-display-name?${params}`);
        const response = await fetch(`${apiUrl}/auth/check-display-name?${params}`);
        
        if (!response.ok) {
          console.error('❌ Response not OK:', response.status, response.statusText);
          throw new Error('Failed to check display name availability');
        }

        const data = await response.json();
        console.log('✅ Response data:', data);
        
        // Only update if the value hasn't changed since we started checking
        if (displayName.trim() === valueToCheck) {
          lastCheckedValueRef.current = valueToCheck;
          const errorMessage = !data.available ? 'This display name is already taken' : null;
          lastErrorRef.current = errorMessage;
          console.log('💾 Setting error message:', errorMessage);
          setDisplayNameError(errorMessage);
        }
      } catch (err) {
        console.error('❌ Error checking display name:', err);
        // On error, don't show error message but also don't clear if value hasn't changed
        if (displayName.trim() === valueToCheck) {
          lastCheckedValueRef.current = valueToCheck;
          lastErrorRef.current = null;
          setDisplayNameError(null);
        }
      } finally {
        if (displayName.trim() === valueToCheck) {
          console.log('🏁 Finished checking, setting checkingDisplayName to false');
          setCheckingDisplayName(false);
        }
      }
    }, 500); // 500ms debounce - wait for user to stop typing

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }
    };
  }, [displayName, isEditingName]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const handleSaveDisplayName = async () => {
    if (!firebaseUser) {
      setError('User not authenticated');
      return;
    }

    // Validate display name is not empty
    if (!displayName.trim()) {
      setError('Display name cannot be empty');
      return;
    }

    // Check if display name is available before saving
    if (displayNameError) {
      setError('Please choose a different display name');
      return;
    }

    // If still checking, wait a bit
    if (checkingDisplayName) {
      setError('Please wait while we check availability...');
      return;
    }

    // Final check before saving
    try {
      const apiUrl = getBackendConfig().apiBaseUrl;
      
      const excludeUserId = user?.id ? user.id.toString() : undefined;
      const params = new URLSearchParams({
        displayName: displayName.trim(),
        ...(excludeUserId && { excludeUserId })
      });

      const response = await fetch(`${apiUrl}/auth/check-display-name?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to verify display name availability');
      }

      const data = await response.json();
      
      if (!data.available) {
        setError('This display name is already taken. Please choose a different one.');
        setDisplayNameError('This display name is already taken');
        return;
      }
    } catch (err) {
      console.error('Error verifying display name:', err);
      setError('Failed to verify display name. Please try again.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    setDisplayNameError(null);

    try {
      await updateProfile(firebaseUser, {
        displayName: displayName.trim()
      });
      
      // Refresh user context to get updated displayName from backend
      await refreshUser();
      
      setSuccess('Display name updated successfully');
      setIsEditingName(false);
      
      // Update local display name state
      setDisplayName(displayName.trim());
    } catch (err: any) {
      setError(err.message || 'Failed to update display name');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (user) {
      setDisplayName(user.displayName || '');
    }
    setIsEditingName(false);
    setError(null);
  };

  const handleChangePassword = async () => {
    if (!firebaseUser || !user) {
      setError('User not authenticated');
      return;
    }

    // Check if user is using email/password authentication
    if (firebaseUser.providerData[0]?.providerId !== 'password') {
      setError('Password change is only available for email/password accounts');
      return;
    }

    setSendingResetEmail(true);
    setError(null);
    setSuccess(null);

    try {
      await sendPasswordReset(user.email);
      setSuccess('Password reset email sent! Please check your email to reset your password.');
      
      // Navigate to forgot password page after a short delay
      setTimeout(() => {
        navigate('/forgot-password');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setSendingResetEmail(false);
    }
  };

  if (authLoading) {
    return (
      <>
        <div className="bg-pattern"></div>
        <Box 
          className="main-container"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh'
          }}
        >
          <AppBar
            position="static"
            elevation={0}
            sx={{ 
              color: 'white',
              flexShrink: 0
            }}
          >
            <Toolbar>
              <Typography 
                variant="h5" 
                component="div" 
                sx={{
                  fontFamily: 'Dancing Script, cursive',
                  fontWeight: 700,
                  fontSize: '30px',
                  cursor: 'pointer',
                  color: 'white',
                  '&:hover': {
                    opacity: 0.8,
                  },
                  ml: 1,
                }}
                onClick={() => {
                  window.location.href = '/';
                }}
              >
                <p style={{ margin: 10, fontFamily: 'Soria, serif', fontWeight: 400 }}>Auditio</p>
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <UserProfile />
            </Toolbar>
          </AppBar>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        </Box>
      </>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <>
      {/* Background pattern */}
      <div className="bg-pattern"></div>
      
      <Box 
        className="main-container"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{ 
            color: 'white',
            flexShrink: 0
          }}
        >
          <Toolbar>
            <Typography 
              variant="h5" 
              component="div" 
              sx={{
                fontFamily: 'Dancing Script, cursive',
                fontWeight: 700,
                fontSize: '30px',
                cursor: 'pointer',
                color: 'white',
                '&:hover': {
                  opacity: 0.8,
                },
                ml: 1,
              }}
              onClick={() => {
                window.location.href = '/';
              }}
            >
              <p style={{ margin: 10, fontFamily: 'Soria, serif', fontWeight: 400 }}>Auditio</p>
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            
            {/* User Profile Component */}
            <UserProfile />
          </Toolbar>
        </AppBar>

        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
          overflow: 'auto'
        }}>
          <Container component="main" maxWidth="md" className="auth-container">
            <Paper className="auth-paper">
              <Box sx={{ mb: 3 }}>
                <Typography component="h1" variant="h5" className="auth-title">
                  Profile
                </Typography>
              </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }} className="auth-alert">
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, width: '100%' }} className="auth-alert">
            {success}
          </Alert>
        )}

        <Box component="form" noValidate>
          {/* Email Field - Read Only */}
          <TextField
            margin="normal"
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            value={email}
            disabled
            className="auth-input"
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 3, borderColor: 'rgba(85, 203, 205, 0.2)' }} />

          {/* Tier Field - Read Only */}
          <TextField
            margin="normal"
            fullWidth
            id="tier"
            label="Tier"
            name="tier"
            value={user?.tier ? user.tier.charAt(0).toUpperCase() + user.tier.slice(1) : 'Free'}
            disabled
            className="auth-input"
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 3, borderColor: 'rgba(85, 203, 205, 0.2)' }} />

          {/* Display Name Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              id="displayName"
              label="Display Name"
              name="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={!isEditingName || saving}
              error={!!displayNameError}
              helperText={
                displayNameError 
                  ? displayNameError 
                  : (checkingDisplayName && isEditingName ? 'Checking availability...' : '')
              }
              className="auth-input"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {!isEditingName ? (
                      <IconButton
                        size="small"
                        onClick={() => setIsEditingName(true)}
                        sx={{ color: 'var(--primary)' }}
                        edge="end"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={handleSaveDisplayName}
                          disabled={saving || !!displayNameError || checkingDisplayName}
                          sx={{ color: 'var(--primary)' }}
                          edge="end"
                        >
                          {saving ? <CircularProgress size={20} /> : <Save fontSize="small" />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          disabled={saving}
                          sx={{ color: 'var(--text-color)' }}
                          edge="end"
                        >
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Password Field - Only show for email/password accounts */}
          {firebaseUser?.providerData[0]?.providerId === 'password' && (
            <>
              <Divider sx={{ my: 3, borderColor: 'rgba(85, 203, 205, 0.2)' }} />

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif', color: 'var(--text-color)', opacity: 0.8 }}>
                    Password
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleChangePassword}
                    disabled={sendingResetEmail}
                    sx={{
                      color: '#ff6b6b',
                      backgroundColor: 'rgba(255, 107, 107, 0.1)',
                      textTransform: 'none',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '0.875rem',
                      padding: '6px 16px',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 107, 107, 0.2)',
                        color: '#ff5252',
                      },
                      '&:disabled': {
                        color: 'rgba(255, 107, 107, 0.5)',
                        backgroundColor: 'rgba(255, 107, 107, 0.05)',
                      }
                    }}
                  >
                    {sendingResetEmail ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    {sendingResetEmail ? 'Sending...' : 'Change Password'}
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  id="password-display"
                  label="Password"
                  name="password-display"
                  value="••••••••"
                  disabled
                  className="auth-input"
                />
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
    </Box>
    </Box>
    </>
  );
};

export default ProfilePage;

